import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { Users, Plus, Search, Building, MapPin, Phone, Mail, Heart, UserPlus, ChevronDown, ChevronUp, ToggleLeft, ToggleRight } from 'lucide-react'
import { collection, query, where, orderBy, getDocs, doc, getDoc } from 'firebase/firestore'
import { db } from '../services/firebase'
import adminService from '../services/adminService'
import groupService from '../services/groupService'

export default function MyGroups() {
  const { currentUser, userProfile } = useAuth()
  const [followedGroups, setFollowedGroups] = useState([])
  const [organizationsWithGroups, setOrganizationsWithGroups] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedOrgs, setExpandedOrgs] = useState(new Set())
  const [showOnlyFollowed, setShowOnlyFollowed] = useState(false)

  useEffect(() => {
    if (currentUser && userProfile) {
      loadUserGroups()
    }
  }, [currentUser, userProfile])

  const loadUserGroups = async () => {
    try {
      setLoading(true)
      console.log('🔍 Loading user groups...')
      
      // Load followed groups
      const followed = await loadFollowedGroups()
      setFollowedGroups(followed)
      
      // Load organizations with their groups
      const orgsWithGroups = await loadOrganizationsWithGroups()
      setOrganizationsWithGroups(orgsWithGroups)
      
      console.log('✅ Loaded', followed.length, 'followed groups and', orgsWithGroups.length, 'organizations')
    } catch (error) {
      console.error('❌ Error loading user groups:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadFollowedGroups = async () => {
    try {
      if (!currentUser) return []
      
      // Get user's followed groups directly from database
      const userRef = doc(db, 'users', currentUser.uid)
      const userDoc = await getDoc(userRef)
      
      if (!userDoc.exists()) {
        console.log('User document not found')
        return []
      }
      
      const userData = userDoc.data()
      
      // Check for groupPreferences map (new structure) or followedGroups array (old structure)
      let followedGroupIds = []
      if (userData.groupPreferences && Object.keys(userData.groupPreferences).length > 0) {
        // New structure: groupPreferences is a map of group names to boolean values
        followedGroupIds = Object.keys(userData.groupPreferences).filter(
          groupName => userData.groupPreferences[groupName] === true
        )
        console.log('🔍 Using groupPreferences structure from main document')
      } else if (userData.followedGroups) {
        // Old structure: followedGroups is an array of group IDs
        followedGroupIds = userData.followedGroups
        console.log('🔍 Using followedGroups structure')
      } else {
        // Try to load from subcollections (mobile app structure)
        console.log('🔍 No groupPreferences or followedGroups found in main document, checking subcollections...')
        followedGroupIds = await loadFollowedGroupsFromSubcollections()
      }
      
      console.log('🔍 User data from database:', userData)
      console.log('🔍 All user data keys:', Object.keys(userData))
      console.log('🔍 Group preferences:', userData.groupPreferences)
      console.log('🔍 Followed groups array:', userData.followedGroups)
      console.log('🔍 Followed organizations:', userData.followedOrganizations)
      console.log('🔍 Followed group names:', followedGroupIds)
      
      if (followedGroupIds.length === 0) {
        console.log('No followed groups found')
        return []
      }
      
      const groups = []
      for (const groupName of followedGroupIds) {
        try {
          console.log('🔍 Loading group data for name:', groupName)
          // Search for group by name in all available groups
          const groupData = await findGroupByName(groupName)
          console.log('🔍 Group data loaded:', groupData)
          if (groupData) {
            groups.push(groupData)
          }
        } catch (error) {
          console.error('Error loading group:', groupName, error)
        }
      }
      
      console.log('🔍 Final followed groups array:', groups)
      console.log('🔍 Group names:', groups.map(g => g.name))
      return groups
    } catch (error) {
      console.error('Error loading followed groups:', error)
      return []
    }
  }

  const loadFollowedGroupsFromSubcollections = async () => {
    try {
      console.log('🔍 Checking subcollections for followed groups...')
      
      // Check for followedGroups subcollection
      const followedGroupsRef = collection(db, 'users', currentUser.uid, 'followedGroups')
      const followedGroupsSnapshot = await getDocs(followedGroupsRef)
      
      if (!followedGroupsSnapshot.empty) {
        console.log('🔍 Found followedGroups subcollection with', followedGroupsSnapshot.size, 'documents')
        const groupNames = []
        followedGroupsSnapshot.forEach(doc => {
          const data = doc.data()
          console.log('🔍 Followed group document:', doc.id, data)
          if (data.name) {
            groupNames.push(data.name)
          }
        })
        return groupNames
      }
      
      // Check for followedOrganizations subcollection (mobile app structure)
      const followedOrgsRef = collection(db, 'users', currentUser.uid, 'followedOrganizations')
      const followedOrgsSnapshot = await getDocs(followedOrgsRef)
      
      if (!followedOrgsSnapshot.empty) {
        console.log('🔍 Found followedOrganizations subcollection with', followedOrgsSnapshot.size, 'documents')
        const groupNames = []
        for (const orgDoc of followedOrgsSnapshot.docs) {
          const orgData = orgDoc.data()
          console.log('🔍 Organization document:', orgDoc.id, orgData)
          
          // Check if this org document has groupPreferences (mobile app structure)
          if (orgData.groupPreferences) {
            console.log('🔍 Found groupPreferences in organization:', orgData.groupPreferences)
            Object.keys(orgData.groupPreferences).forEach(groupName => {
              if (orgData.groupPreferences[groupName] === true) {
                groupNames.push(groupName)
                console.log('🔍 Added group from org groupPreferences:', groupName)
              }
            })
          }
          
          // Also check the old groups structure for backward compatibility
          if (orgData.groups) {
            Object.keys(orgData.groups).forEach(groupName => {
              if (orgData.groups[groupName] === true) {
                groupNames.push(groupName)
                console.log('🔍 Added group from org groups:', groupName)
              }
            })
          }
        }
        return groupNames
      }
      
      console.log('🔍 No subcollections found with followed groups')
      return []
    } catch (error) {
      console.error('Error loading followed groups from subcollections:', error)
      return []
    }
  }


  const findGroupByName = async (groupName) => {
    try {
      // Get all organizations first
      const orgsQuery = query(collection(db, 'organizations'))
      const orgsSnapshot = await getDocs(orgsQuery)
      
      for (const orgDoc of orgsSnapshot.docs) {
        try {
          const groupsQuery = query(
            collection(db, 'organizations', orgDoc.id, 'groups'),
            where('isActive', '==', true)
          )
          const groupsSnapshot = await getDocs(groupsQuery)
          
          for (const groupDoc of groupsSnapshot.docs) {
            const groupData = groupDoc.data()
            if (groupData.name === groupName) {
              return {
                id: groupDoc.id,
                ...groupData,
                organizationId: orgDoc.id,
                organizationName: orgDoc.data().name
              }
            }
          }
        } catch (error) {
          console.error('Error searching groups in org:', orgDoc.id, error)
        }
      }
      
      return null
    } catch (error) {
      console.error('Error finding group by name:', error)
      return null
    }
  }

  const loadOrganizationsWithGroups = async () => {
    try {
      if (!currentUser) return []
      
      // Get user's followed organizations first
      const userRef = doc(db, 'users', currentUser.uid)
      const userDoc = await getDoc(userRef)
      
      if (!userDoc.exists()) {
        console.log('User document not found')
        return []
      }
      
      const userData = userDoc.data()
      const followedOrgIds = userData.followedOrganizations || []
      
      if (followedOrgIds.length === 0) {
        console.log('No followed organizations found - no groups to show')
        return []
      }
      
      console.log('🔍 Loading organizations with groups:', followedOrgIds)
      
      const organizationsWithGroups = []
      for (const orgId of followedOrgIds) {
        try {
          // Get organization details
          const orgRef = doc(db, 'organizations', orgId)
          const orgDoc = await getDoc(orgRef)
          
          if (!orgDoc.exists()) {
            console.log('Organization not found:', orgId)
            continue
          }
          
          const orgData = orgDoc.data()
          
          // Get groups for this organization using groupService with access control
          const groups = await groupService.getGroupsForOrganization(orgId, currentUser.uid, userProfile)
          
          // Add organization info to each group
          const groupsWithOrgInfo = groups.map(group => ({
            ...group,
            organizationId: orgId,
            organizationName: orgData.name
          }))
          
          if (groupsWithOrgInfo.length > 0) {
            organizationsWithGroups.push({
              id: orgId,
              name: orgData.name,
              description: orgData.description,
              address: orgData.address,
              city: orgData.city,
              state: orgData.state,
              zipCode: orgData.zipCode,
              phone: orgData.phone,
              email: orgData.email,
              website: orgData.website,
              groups: groupsWithOrgInfo
            })
          }
          
          console.log(`✅ Loaded ${groupsWithOrgInfo.length} groups from organization: ${orgData.name}`)
        } catch (error) {
          console.error('Error loading groups for org:', orgId, error)
        }
      }
      
      console.log(`✅ Total organizations with groups: ${organizationsWithGroups.length}`)
      return organizationsWithGroups
    } catch (error) {
      console.error('Error loading organizations with groups:', error)
      return []
    }
  }

  const toggleFollowGroup = async (groupId) => {
    try {
      // Find the group in any organization
      let group = null
      for (const org of organizationsWithGroups) {
        group = org.groups.find(g => g.id === groupId)
        if (group) break
      }
      
      if (!group) {
        console.error('Group not found:', groupId)
        return
      }

      const isFollowing = followedGroups.some(g => g.name === group.name)
      
      if (isFollowing) {
        // Unfollow group
        await adminService.unfollowGroup(groupId)
        setFollowedGroups(prev => prev.filter(g => g.name !== group.name))
        console.log('✅ Unfollowed group:', group.name)
      } else {
        // Follow group
        await adminService.followGroup(groupId)
        setFollowedGroups(prev => [...prev, group])
        console.log('✅ Followed group:', group.name)
      }
      
      // Force refresh user profile to sync with mobile app
      if (window.forceUpdateUserProfile) {
        await window.forceUpdateUserProfile()
      }
      
      console.log('📱 Group preferences updated - mobile app will sync automatically')
    } catch (error) {
      console.error('❌ Error toggling group follow:', error)
      alert('Error updating group follow status. Please try again.')
    }
  }

  const toggleOrgExpansion = (orgId) => {
    setExpandedOrgs(prev => {
      const newSet = new Set(prev)
      if (newSet.has(orgId)) {
        newSet.delete(orgId)
      } else {
        newSet.add(orgId)
      }
      return newSet
    })
  }

  const isFollowingGroup = (groupId) => {
    // Find the group by ID to get its name
    let group = null
    for (const org of organizationsWithGroups) {
      group = org.groups.find(g => g.id === groupId)
      if (group) break
    }
    if (!group) return false
    
    // Check if the group name is in the followed groups list
    return followedGroups.some(g => g.name === group.name)
  }

  const getFilteredOrganizations = () => {
    if (!searchTerm) return organizationsWithGroups
    
    return organizationsWithGroups.filter(org => {
      // Check if organization name matches
      if (org.name.toLowerCase().includes(searchTerm.toLowerCase())) {
        return true
      }
      
      // Check if any group name matches
      return org.groups.some(group => 
        group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (group.description && group.description.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    })
  }

  const getFilteredGroups = (groups) => {
    if (!searchTerm) return groups
    
    return groups.filter(group => 
      group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (group.description && group.description.toLowerCase().includes(searchTerm.toLowerCase()))
    )
  }


  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">My Groups</h1>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  const filteredOrganizations = getFilteredOrganizations()

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Groups</h1>
          <p className="text-gray-600 mt-1">
            Manage which groups you follow from organizations you're following
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowOnlyFollowed(!showOnlyFollowed)}
            className={`px-4 py-2 rounded-md font-medium transition-colors ${
              showOnlyFollowed 
                ? 'bg-blue-600 text-white hover:bg-blue-700' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {showOnlyFollowed ? 'Show All Groups' : 'Show Only Followed'}
          </button>
          <button
            onClick={loadUserGroups}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
          >
            Refresh Data
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <Users className="h-8 w-8 text-blue-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Groups Following</p>
              <p className="text-2xl font-bold text-gray-900">{followedGroups.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <Building className="h-8 w-8 text-green-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Organizations</p>
              <p className="text-2xl font-bold text-gray-900">{organizationsWithGroups.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <Heart className="h-8 w-8 text-red-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Total Groups Available</p>
              <p className="text-2xl font-bold text-gray-900">
                {organizationsWithGroups.reduce((total, org) => total + org.groups.length, 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search organizations or groups..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Organizations with Groups */}
      <div className="space-y-4">
        {filteredOrganizations.map((organization) => {
          const isExpanded = expandedOrgs.has(organization.id)
          const filteredGroups = getFilteredGroups(organization.groups)
          const followedGroupsInOrg = filteredGroups.filter(group => 
            followedGroups.some(fg => fg.name === group.name)
          )
          
          return (
            <div key={organization.id} className="bg-white rounded-lg shadow-sm border border-gray-200">
              {/* Organization Header */}
              <div 
                className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => toggleOrgExpansion(organization.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Building className="h-6 w-6 text-blue-600" />
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{organization.name}</h3>
                      <p className="text-sm text-gray-600">
                        {filteredGroups.length} group{filteredGroups.length !== 1 ? 's' : ''} 
                        {followedGroupsInOrg.length > 0 && (
                          <span className="ml-2 text-blue-600">
                            ({followedGroupsInOrg.length} following)
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {isExpanded ? (
                      <ChevronUp className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Groups Dropdown */}
              {isExpanded && (
                <div className="border-t border-gray-200">
                  {filteredGroups.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">
                      <Users className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                      <p>No groups found</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-200">
                      {filteredGroups.map((group) => {
                        const isFollowing = isFollowingGroup(group.id)
                        const shouldShow = showOnlyFollowed ? isFollowing : true
                        
                        if (!shouldShow) return null
                        
                        return (
                          <div key={group.id} className="p-4 hover:bg-gray-50">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-1">
                                  <h4 className="font-medium text-gray-900">{group.name}</h4>
                                  {isFollowing && (
                                    <Heart className="h-4 w-4 text-red-500" />
                                  )}
                                </div>
                                
                                {group.description && (
                                  <p className="text-sm text-gray-600 mb-2">{group.description}</p>
                                )}
                                
                                <div className="flex items-center space-x-4 text-xs text-gray-500">
                                  <div className="flex items-center">
                                    <Users className="h-3 w-3 mr-1" />
                                    <span>{group.memberCount || 0} members</span>
                                  </div>
                                  <div className="flex items-center">
                                    <Heart className="h-3 w-3 mr-1 text-red-400" />
                                    <span>{group.followerCount ?? 0} following</span>
                                  </div>
                                  
                                  {group.contact && (
                                    <div className="flex items-center">
                                      <Phone className="h-3 w-3 mr-1" />
                                      <span>
                                        {typeof group.contact === 'string' 
                                          ? group.contact 
                                          : group.contact.phone || group.contact.email || 'Contact available'
                                        }
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              <div className="ml-4">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    toggleFollowGroup(group.id)
                                  }}
                                  className={`flex items-center space-x-2 px-3 py-2 rounded-md font-medium transition-colors ${
                                    isFollowing
                                      ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                      : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                  }`}
                                >
                                  {isFollowing ? (
                                    <>
                                      <ToggleRight className="h-4 w-4" />
                                      <span>Following</span>
                                    </>
                                  ) : (
                                    <>
                                      <ToggleLeft className="h-4 w-4" />
                                      <span>Follow</span>
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
        
        {filteredOrganizations.length === 0 && searchTerm && (
          <div className="text-center py-8">
            <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No organizations or groups found</h3>
            <p className="text-gray-600">Try adjusting your search terms</p>
          </div>
        )}
        
        {organizationsWithGroups.length === 0 && !searchTerm && (
          <div className="text-center py-8">
            <Building className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No organizations followed</h3>
            <p className="text-gray-600 mb-4">You need to follow organizations first to see their groups</p>
            <a
              href="/discover"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors inline-block"
            >
              Discover Organizations
            </a>
          </div>
        )}
      </div>
    </div>
  )
}
